import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, ArrowLeft, Trash2 } from 'lucide-react';
import Layout from '../components/layout/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import ListingCard from '../components/ListingCard';
import { getFavorites, removeFavorite } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const FavoritesPage = () => {
    const { advertiser, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [favorites, setFavorites] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!authLoading && !advertiser) {
            navigate('/advertiser/login');
        }
    }, [advertiser, authLoading, navigate]);

    useEffect(() => {
        const fetchFavorites = async () => {
            if (!advertiser) return;
            try {
                const res = await getFavorites();
                setFavorites(res.data);
            } catch (err) {
                console.error('Failed to load favorites:', err);
            } finally {
                setLoading(false);
            }
        };
        if (advertiser) {
            fetchFavorites();
        }
    }, [advertiser]);

    const handleRemoveFavorite = async (listingId) => {
        try {
            await removeFavorite(listingId);
            setFavorites(favorites.filter(f => f.id !== listingId));
            toast.success('Removed from favorites');
        } catch (err) {
            toast.error('Failed to remove from favorites');
        }
    };

    if (authLoading || !advertiser) {
        return (
            <Layout>
                <div className="min-h-[60vh] flex items-center justify-center">
                    <div className="spinner" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="bg-gray-50 min-h-screen" data-testid="favorites-page">
                {/* Header */}
                <div className="bg-navy text-white py-8">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <Link to="/dashboard" className="inline-flex items-center text-gray-300 hover:text-white mb-4">
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Back to Dashboard
                        </Link>
                        <div className="flex items-center gap-3">
                            <Heart className="w-8 h-8 text-gold fill-gold" />
                            <h1 className="font-heading text-2xl sm:text-3xl font-bold">
                                My Favorites
                            </h1>
                        </div>
                        <p className="text-gray-300 mt-2">
                            {favorites.length} saved listing{favorites.length !== 1 ? 's' : ''}
                        </p>
                    </div>
                </div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="spinner" />
                        </div>
                    ) : favorites.length > 0 ? (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {favorites.map(listing => (
                                <div key={listing.id} className="relative group">
                                    <ListingCard listing={listing} />
                                    <Button
                                        size="sm"
                                        variant="destructive"
                                        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleRemoveFavorite(listing.id);
                                        }}
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <Card>
                            <CardContent className="p-12 text-center">
                                <Heart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                <h3 className="text-xl font-semibold text-gray-800 mb-2">No favorites yet</h3>
                                <p className="text-gray-500 mb-6">
                                    Save listings you're interested in by clicking the heart icon
                                </p>
                                <Link to="/listings">
                                    <Button className="bg-navy hover:bg-navy/90">
                                        Browse Listings
                                    </Button>
                                </Link>
                            </CardContent>
                        </Card>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default FavoritesPage;
